import { useEffect } from 'react';

import { useRutilusLazyQuery } from '../../../../../api/graphql/useRutilusLazyQuery';
import { reportMykissClientError } from '../../../../../helpers/errorHandling/client';
import { getNodesFromConnection } from '../../../../../helpers/graphql';
import { UseUserInfo, useUserInfo } from '../../../../../helpers/hooks/useUserInfo';
import { GetCatchDetails, GetCatchDetailsVariables } from '../../../../../interfaces/graphql';
import { getCatchDetails } from '../../../../../queries/rutilus/Catch';
import type { CatchConditions } from '../../../types/CatchConditions';
import type { CatchGearItem } from '../../../types/CatchGearItem';
import type { CatchPost } from '../../../types/CatchPost';
import type { Trip } from '../../../types/Trip';

export interface CatchDetails {
  catchConditions: CatchConditions | null | undefined;
  catchGear: CatchGearItem[];
  catchPost: CatchPost | null | undefined;
  currentUser: UseUserInfo;
  trip: Trip | null | undefined;
}

interface CatchDetailsData {
  catchDetails: CatchDetails;
  isLoading: boolean;
}

export const useCatchDetails = (currentPostId: string | undefined): CatchDetailsData => {
  const currentUser = useUserInfo();
  const { isLoggedIn, isLoadingUser } = currentUser;

  const [fetchCatchDetails, { data, loading: isCatchDetailsLoading }] = useRutilusLazyQuery<
    GetCatchDetails,
    GetCatchDetailsVariables
  >(getCatchDetails, {
    skip: !currentPostId,
    variables: { externalId: currentPostId },
    fetchPolicy: 'network-only', // TODO: check why default cache-first doesn't work - https://fishbrain.atlassian.net/browse/FIB-39752
    nextFetchPolicy: 'network-only', // TODO: check why default cache-first doesn't work - https://fishbrain.atlassian.net/browse/FIB-39752
  });

  useEffect(() => {
    fetchCatchDetails().catch(reportMykissClientError);
  }, [currentUser.isPremium, fetchCatchDetails]);

  const catchDetails = data?.catchDetails;
  const catchConditions = isLoggedIn ? catchDetails?.catchConditions : undefined;
  const catchPost = catchDetails?.catchPost;
  const trip = catchDetails?.trip;
  const catchGear = getNodesFromConnection(catchDetails?.catchGear);
  const isLoading = isLoadingUser || isCatchDetailsLoading;

  return {
    catchDetails: { catchGear, trip, currentUser, catchPost, catchConditions },
    isLoading,
  };
};
